// Formatação para mostrar na tela (padrão brasileiro) e leitura do que o
// usuário digita.
import { apenasDigitos, normalizarDocumento } from './validacao.js';

const moeda = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const numero2 = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const numeroLivre = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 });

export function dinheiro(valor) {
  if (valor === null || valor === undefined || valor === '') return '';
  return moeda.format(Number(valor));
}

export function numeroBR(valor, { casas = null } = {}) {
  if (valor === null || valor === undefined || valor === '') return '';
  return casas === 2 ? numero2.format(Number(valor)) : numeroLivre.format(Number(valor));
}

// Litros com até 3 casas (a bomba mostra 3): 379,456
const litrosBR = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 3 });
export function litros(valor) {
  if (valor === null || valor === undefined || valor === '') return '';
  return litrosBR.format(Number(valor));
}

export function percentual(valor) {
  if (valor === null || valor === undefined || valor === '') return '';
  return `${numeroLivre.format(Number(valor))}%`;
}

// Lê "1.234,56", "1234,56", "13,5", "13.5" ou "13" → número. Vazio → null.
// Devolve NaN quando não é um número.
export function lerNumero(texto) {
  let t = String(texto ?? '').trim().replace(/\s|R\$|%/g, '');
  if (t === '') return null;
  if (t.includes(',')) {
    t = t.replace(/\./g, '').replace(',', '.');
  } else if (/^\d{1,3}(\.\d{3})+$/.test(t)) {
    t = t.replace(/\./g, '');
  }
  if (!/^-?\d+(\.\d+)?$/.test(t)) return NaN;
  return Number(t);
}

export function lerInteiro(texto) {
  const t = String(texto ?? '').trim().replace(/\./g, '');
  if (t === '') return null;
  if (!/^\d+$/.test(t)) return NaN;
  return Number(t);
}

export function cpf(texto) {
  const d = apenasDigitos(texto);
  if (d.length !== 11) return texto ?? '';
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

export function cnpj(texto) {
  const c = normalizarDocumento(texto);
  if (c.length !== 14) return texto ?? '';
  return `${c.slice(0, 2)}.${c.slice(2, 5)}.${c.slice(5, 8)}/${c.slice(8, 12)}-${c.slice(12)}`;
}

export function documento(texto) {
  const c = normalizarDocumento(texto);
  if (c.length === 11) return cpf(c);
  if (c.length === 14) return cnpj(c);
  return texto ?? '';
}

// "5548999990000" → "(48) 99999-0000"
export function celular(texto) {
  let d = apenasDigitos(texto);
  if ((d.length === 12 || d.length === 13) && d.startsWith('55')) d = d.slice(2);
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return texto ?? '';
}

// Mercosul fica como está (ABC1D23); a antiga ganha o hífen (ABC-1234).
export function placa(texto) {
  const p = normalizarDocumento(texto);
  if (/^[A-Z]{3}[0-9]{4}$/.test(p)) return `${p.slice(0, 3)}-${p.slice(3)}`;
  return p;
}

// "2026-09-28" → "28/09/2026"
export function data(iso) {
  if (!iso) return '';
  const [a, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}/${a}`;
}

// Um momento (data e hora) como data no fuso do aparelho: "06/10/2026".
export function dataDoMomento(momento) {
  if (!momento) return '';
  const d = new Date(momento);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// "28/09/2026" → "2026-09-28" (ou null)
export function lerData(texto) {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(texto ?? '').trim());
  if (!m) return null;
  return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
}

export function plural(n, singular, pluralTexto = `${singular}s`) {
  return `${n} ${n === 1 ? singular : pluralTexto}`;
}

const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

// "2026-09" ou "2026-09-01" → "setembro de 2026"
export function mesPorExtenso(iso) {
  if (!iso) return '';
  const [a, m] = String(iso).split('-').map(Number);
  return `${MESES[m - 1]} de ${a}`;
}

// "2026-09-01" → "set/2026"
export function mesCurto(iso) {
  if (!iso) return '';
  const [a, m] = String(iso).split('-').map(Number);
  return `${MESES[m - 1].slice(0, 3)}/${a}`;
}
